import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import api from "@/lib/api";
import { Bell, Zap, Crown, Megaphone, CheckCheck, ShieldCheck, MapPin, IndianRupee, Calendar, X, UserPlus, Heart, FileVideo, Settings2, Bookmark, Clock, Archive } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { catLabel } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ICONS = { challenge: Zap, payment: Crown, casting: Megaphone, referral: Crown, verification: ShieldCheck, follow: UserPlus, like: Heart, upload: FileVideo };

export default function Notifications() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [casting, setCasting] = useState([]);
  const [locked, setLocked] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prefs, setPrefs] = useState({ follows: true, likes: true, uploads: true });
  const [oppFilter, setOppFilter] = useState("all"); // all | saved | expiring | seasonal | weekly | archive
  const [opps, setOpps] = useState([]);

  const loadOpps = async (f) => {
    try {
      if (f === "archive") {
        const { data } = await api.get("/opportunities/archive");
        setOpps(data.opportunities || []);
      } else {
        const params = f === "all" ? {} : { filter: f };
        const { data } = await api.get("/opportunities", { params });
        setOpps(data.opportunities || []);
      }
    } catch { setOpps([]); }
  };

  const load = async () => {
    const { data } = await api.get("/notifications");
    setNotifs(data.notifications || []);
    const { data: cast } = await api.get("/casting");
    setCasting(cast.roles || []);
    setLocked(cast.locked);
    try { const { data: p } = await api.get("/notifications/settings"); setPrefs(p); } catch {}
    loadOpps(oppFilter);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadOpps(oppFilter); /* eslint-disable-next-line */ }, [oppFilter]);

  const saveOpp = async (op, e) => {
    e?.stopPropagation();
    try {
      const { data } = await api.post(`/opportunities/${op.id}/save`);
      toast.success(data.saved ? "Saved" : "Removed");
      loadOpps(oppFilter);
    } catch { toast.error("Failed"); }
  };

  const markRead = async (id, e) => {
    e?.stopPropagation();
    await api.post(`/notifications/${id}/read`);
    setNotifs(notifs.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllRead = async () => {
    try { await api.post("/notifications/mark-all-read"); setNotifs(notifs.map(n => ({ ...n, read: true }))); toast.success("All caught up"); }
    catch {}
  };

  const savePrefs = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try { await api.patch("/notifications/settings", patch); toast.success("Saved"); }
    catch { toast.error("Save failed"); setPrefs(prefs); }
  };

  const onNotifClick = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
      setNotifs(notifs.map(x => x.id === n.id ? { ...x, read: true } : x));
    }
    if (n.type === "casting") {
      const roleId = n.meta?.role_id;
      const role = casting.find(r => r.id === roleId);
      if (role) setSelectedRole(role);
      else if (locked) nav("/app/plans");
    } else if (n.type === "challenge") {
      const vid = n.meta?.video_id;
      if (vid) nav(`/v/${vid}`);
    } else if (n.type === "verification") {
      nav(user?.is_verified ? "/app/story-card" : "/app/verification");
    } else if (n.type === "payment") {
      nav("/app/me");
    } else if (n.type === "referral") {
      nav("/app/referral");
    } else if (n.type === "follow") {
      const uname = n.meta?.actor_username;
      if (uname) nav(`/${uname}`);
    } else if (n.type === "like" || n.type === "upload") {
      const vid = n.meta?.video_id;
      if (vid) nav(`/v/${vid}`);
    }
  };

  const onRoleClick = (role) => {
    if (role.locked) { nav("/app/plans"); return; }
    setSelectedRole(role);
  };

  return (
    <div data-testid="notifications-page" className="min-h-screen pb-24">
      <TopBar subtitle="Notifications"/>
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-6">
        <BackButton fallback="/app" label="Back"/>

        <section>
          <div className="flex items-center gap-2 mb-3">
            <Megaphone size={16} className="text-[#DC2626]"/>
            <h2 className="font-display text-lg font-semibold">Casting opportunities</h2>
          </div>

          <div data-testid="opp-filters" className="flex flex-wrap gap-1.5 mb-3">
            {[
              {k: "all", label: "All"},
              {k: "saved", label: "Saved"},
              {k: "expiring", label: "Expiring ≤3d"},
              {k: "seasonal", label: "Seasonal"},
              {k: "weekly", label: "Weekly drops"},
              {k: "archive", label: "Archive"},
            ].map(f => (
              <button key={f.k} data-testid={`opp-filter-${f.k}`} onClick={() => setOppFilter(f.k)} className={`h-8 px-3 rounded-full text-[11px] font-medium border ${oppFilter === f.k ? "border-[#DC2626] bg-[#DC2626]/15 text-[#DC2626]" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-[#DC2626]/40"}`}>{f.label}</button>
            ))}
          </div>

          {locked && oppFilter === "all" && (
            <div data-testid="casting-locked" className="rounded-2xl border border-[#DC2626]/40 bg-[#DC2626]/5 p-4 mb-3">
              <p className="text-sm text-zinc-300">Free users see teasers only. Upgrade to Plus or Pro to unlock full casting briefs + WhatsApp alerts.</p>
              <Link to="/app/plans" className="mt-3 inline-flex rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white px-4 h-9 items-center font-semibold text-sm">Upgrade now</Link>
            </div>
          )}

          {opps.length === 0 ? (
            <p className="text-sm text-zinc-400 mt-3">{oppFilter === "saved" ? "No saved opportunities yet — tap the bookmark icon to save one." : oppFilter === "archive" ? "No archived opportunities yet." : "No open roles right now. Check back soon."}</p>
          ) : (
            <div className="mt-1 space-y-2">
              {opps.map((r) => <OpportunityCard key={r.id} role={r} onOpen={() => onRoleClick(r)} onSave={(e) => saveOpp(r, e)}/>)}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center gap-2 mb-3">
            <Bell size={16} className="text-[#DC2626]"/>
            <h2 className="font-display text-lg font-semibold">Your notifications</h2>
            <div className="ml-auto flex gap-2">
              <button data-testid="notif-settings-btn" onClick={() => setSettingsOpen(true)} className="text-[11px] text-zinc-400 hover:text-white inline-flex items-center gap-1"><Settings2 size={12}/> Settings</button>
              {notifs.some(n => !n.read) && (
                <button data-testid="mark-all-read" onClick={markAllRead} className="text-[11px] text-[#DC2626] hover:text-[#EF4444] inline-flex items-center gap-1"><CheckCheck size={12}/> Mark all read</button>
              )}
            </div>
          </div>
          {notifs.length === 0 ? (
            <p className="text-sm text-zinc-400">Nothing yet. Post a performance to get things moving.</p>
          ) : (
            <div className="space-y-2">
              {notifs.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                return (
                  <button
                    key={n.id}
                    data-testid={`notif-${n.id}`}
                    onClick={() => onNotifClick(n)}
                    className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 hover:border-[#DC2626]/50 transition-colors ${n.read ? "border-zinc-800 bg-[#180D0D]" : "border-[#DC2626]/40 bg-[#DC2626]/5"}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-[#DC2626]/15 text-[#DC2626] flex items-center justify-center shrink-0">
                      <Icon size={16}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">{n.title}</p>
                      <p className="text-xs text-zinc-300 mt-0.5">{n.message}</p>
                    </div>
                    {!n.read && (
                      <span onClick={(e) => markRead(n.id, e)} data-testid={`notif-read-${n.id}`} className="text-zinc-400 hover:text-[#DC2626] transition-colors shrink-0">
                        <CheckCheck size={16}/>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="notif-settings-modal" className="bg-[#180D0D] border-zinc-800 text-white max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Notification settings</DialogTitle></DialogHeader>
          <p className="text-[11px] text-zinc-400 mb-2">
            {prefs.push_daily_cap ? `Max ${prefs.push_daily_cap} push messages per day (${prefs.plan === "free" ? "Free" : "Creator Pass"}).` : "In-app notifications always show."}
            {" "}Quiet hours: 11 PM – 8 AM (IST) — only urgent alerts.
          </p>
          <p className="text-[10px] uppercase tracking-wider text-[#F5C462] mt-2 mb-1">Master toggles</p>
          {[
            {k: "social_alerts", label: "Social alerts (likes, comments, follows, new uploads)"},
            {k: "weekly_summaries", label: "Weekly opportunity summary in your category"},
            {k: "creator_pass_reminders", label: "Creator Pass renewal & benefit reminders"},
            {k: "quiet_hours_enabled", label: "Respect quiet hours (11 PM – 8 AM IST)"},
          ].map(o => (
            <label key={o.k} className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 cursor-pointer">
              <span className="text-sm text-white pr-2">{o.label}</span>
              <input data-testid={`pref-${o.k}`} type="checkbox" checked={prefs[o.k] !== false} onChange={(e) => savePrefs({ [o.k]: e.target.checked })} className="accent-[#DC2626] w-4 h-4"/>
            </label>
          ))}
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-3 mb-1">Fine-grained (social)</p>
          {[{k: "follows", label: "New follower"}, {k: "likes", label: "Someone likes my performance"}, {k: "comments", label: "New comment"}, {k: "uploads", label: "New upload from creators I follow"}].map(o => (
            <label key={o.k} className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 cursor-pointer">
              <span className="text-sm text-white">{o.label}</span>
              <input data-testid={`pref-${o.k}`} type="checkbox" checked={prefs[o.k] !== false} onChange={(e) => savePrefs({ [o.k]: e.target.checked })} className="accent-[#DC2626] w-4 h-4"/>
            </label>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedRole} onOpenChange={(v) => !v && setSelectedRole(null)}>
        <DialogContent data-testid="casting-detail-modal" className="bg-[#180D0D] border-zinc-800 text-white max-w-lg">
          {selectedRole && (
            <>
              <div className="rounded-xl border border-[#F5C462]/30 bg-[#F5C462]/5 p-2.5 mt-3">
                <p className="text-[10px] text-zinc-400 leading-relaxed">
                  <span className="text-[#F5C462] font-semibold">Note:</span> Participation does not guarantee selection for any project.
                </p>
              </div>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl pr-8">{selectedRole.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs rounded-full bg-[#DC2626]/15 text-[#DC2626] border border-[#DC2626]/30 px-2.5 py-1">{catLabel(selectedRole.category)}</span>
                  {selectedRole.city && (
                    <span className="text-xs rounded-full bg-zinc-800 text-zinc-200 border border-zinc-700 px-2.5 py-1 inline-flex items-center gap-1">
                      <MapPin size={11}/> {selectedRole.city}
                    </span>
                  )}
                  {selectedRole.budget && (
                    <span className="text-xs rounded-full bg-zinc-800 text-zinc-200 border border-zinc-700 px-2.5 py-1 inline-flex items-center gap-1">
                      <IndianRupee size={11}/> {selectedRole.budget}
                    </span>
                  )}
                  {selectedRole.deadline && (
                    <span className="text-xs rounded-full bg-zinc-800 text-zinc-200 border border-zinc-700 px-2.5 py-1 inline-flex items-center gap-1">
                      <Calendar size={11}/> Apply by {selectedRole.deadline}
                    </span>
                  )}
                </div>
                <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-line">{selectedRole.description}</p>
                <div className="pt-2">
                  <Button
                    data-testid="role-apply"
                    onClick={() => { toast.success("Your profile has been shared with the caster. They'll reach out on your registered contact."); setSelectedRole(null); }}
                    className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold"
                  >
                    Apply with my NextScene profile
                  </Button>
                  <p className="text-[11px] text-zinc-400 text-center mt-2">The caster gets your name, city, category and public videos.</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Countdown-timer opportunity card w/ save + archived badge ----
function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!iso) return;
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, [iso]);
  if (!iso) return null;
  const ms = new Date(iso).getTime() - now;
  if (ms <= 0) return "expired";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d >= 1) return `${d}d ${h}h left`;
  if (h >= 1) return `${h}h ${m}m left`;
  return `${m}m left`;
}

function OpportunityCard({ role, onOpen, onSave }) {
  const countdown = useCountdown(role.expiry);
  const urgent = countdown && countdown !== "expired" && (countdown.startsWith("0h") || countdown.endsWith("m left"));
  return (
    <button
      data-testid={`casting-${role.id}`}
      onClick={onOpen}
      className={`w-full text-left rounded-xl border p-4 hover:border-[#DC2626]/50 transition-colors cursor-pointer ${role.expired ? "border-zinc-800 bg-zinc-900/30 opacity-70" : role.is_seasonal ? "border-[#F5C462]/40 bg-[#F5C462]/5" : "border-zinc-800 bg-[#180D0D]"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-white flex items-center gap-2 flex-wrap">
            {role.title}
            {role.is_seasonal && <span className="text-[9px] uppercase tracking-wider rounded-full bg-[#F5C462]/15 text-[#F5C462] border border-[#F5C462]/30 px-2 py-0.5">Seasonal</span>}
            {role.drop_week && <span className="text-[9px] uppercase tracking-wider rounded-full bg-[#DC2626]/15 text-[#DC2626] border border-[#DC2626]/30 px-2 py-0.5">Weekly</span>}
            {role.expired && <span className="text-[9px] uppercase tracking-wider rounded-full bg-zinc-700/40 text-zinc-300 border border-zinc-700 px-2 py-0.5 inline-flex items-center gap-1"><Archive size={9}/>Expired</span>}
          </p>
          <p className="text-xs text-[#DC2626] mt-0.5">{catLabel(role.category)}{role.city ? ` · ${role.city}` : ""}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {role.locked && <span className="text-[10px] text-[#DC2626]">🔒 Locked</span>}
          {onSave && (
            <button data-testid={`opp-save-${role.id}`} onClick={onSave} className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${role.saved ? "bg-[#DC2626]/15 text-[#DC2626]" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white"}`}>
              <Bookmark size={13} fill={role.saved ? "#DC2626" : "none"}/>
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-zinc-300 mt-2 leading-relaxed line-clamp-2">{role.description || (role.locked ? "Upgrade to unlock the full brief." : "")}</p>
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        {role.deadline && <p className="text-xs text-zinc-400">Apply by: {role.deadline}</p>}
        {countdown && countdown !== "expired" && (
          <span className={`text-[11px] rounded-full px-2 py-0.5 border inline-flex items-center gap-1 ${urgent ? "border-[#DC2626] bg-[#DC2626]/15 text-[#DC2626] animate-pulse" : "border-zinc-700 bg-zinc-900/60 text-zinc-300"}`}>
            <Clock size={10}/> {countdown}
          </span>
        )}
      </div>
      {!role.expired && <p className="text-[11px] text-[#DC2626] mt-2">Tap to see full brief →</p>}
    </button>
  );
}
